import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Merge, AlertTriangle, ArrowDown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { UnifiedInvoice } from '@/types/invoicing';
import { formatCurrency } from '@/utils/document';

interface MergeInvoicesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInvoices: Set<number>;
  invoices: UnifiedInvoice[];
  clientMap: Record<number, string>;
  matterMap: Record<number, string>;
  onMergeComplete: () => void;
}

export function MergeInvoicesDialog({
  isOpen,
  onClose,
  selectedInvoices,
  invoices,
  clientMap,
  matterMap,
  onMergeComplete,
}: MergeInvoicesDialogProps) {
  const [isMerging, setIsMerging] = useState(false);

  // Get the selected invoice objects, sorted by invoice number
  const selectedList = useMemo(() => {
    return invoices
      .filter((inv) => selectedInvoices.has(inv.id))
      .sort((a, b) => a.invoice_number.localeCompare(b.invoice_number, undefined, { numeric: true }));
  }, [invoices, selectedInvoices]);

  // Validation: all must be same client, draft, type=invoice
  const validation = useMemo(() => {
    if (selectedList.length < 2) {
      return { valid: false, error: 'Select at least 2 invoices to merge.' };
    }

    const clients = new Set(selectedList.map((inv) => inv.client));
    if (clients.size > 1) {
      return { valid: false, error: 'All selected invoices must belong to the same client.' };
    }

    const nonInvoices = selectedList.filter((inv) => inv.invoice_type !== 'invoice');
    if (nonInvoices.length > 0) {
      return {
        valid: false,
        error: `Only regular invoices can be merged. ${nonInvoices.map((i) => i.invoice_number).join(', ')} are not regular invoices.`,
      };
    }

    const nonDraft = selectedList.filter((inv) => inv.status !== 'draft');
    if (nonDraft.length > 0) {
      return {
        valid: false,
        error: `Only draft invoices can be merged. ${nonDraft.map((i) => i.invoice_number).join(', ')} are not drafts.`,
      };
    }

    return { valid: true, error: null };
  }, [selectedList]);

  const target = selectedList[0];
  const sources = selectedList.slice(1);

  const mergedTotal = useMemo(() => {
    return selectedList.reduce((sum, inv) => sum + Number(inv.total || 0), 0);
  }, [selectedList]);

  const handleMerge = async () => {
    if (!validation.valid) return;

    setIsMerging(true);
    try {
      const ids = selectedList.map((inv) => inv.id);
      await UnifiedInvoiceAPI.mergeInvoices(ids);

      toast({
        title: 'Invoices merged',
        description: `${selectedList.length} invoices merged into ${target.invoice_number}. Invoice numbers have been renumbered.`,
      });

      onMergeComplete();
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Unknown error';
      toast({
        title: 'Merge failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Merge Invoices
          </DialogTitle>
          <DialogDescription>
            Merge {selectedList.length} invoices into a single invoice. Timesheets, costs, and amounts will be combined.
            Subsequent invoice numbers will be shifted down to fill the gaps.
          </DialogDescription>
        </DialogHeader>

        {!validation.valid ? (
          <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
            <p className="text-sm text-destructive">{validation.error}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Target invoice */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Target invoice (keeps the number)</h4>
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono font-semibold">{target.invoice_number}</span>
                    <span className="text-muted-foreground ml-2">
                      {clientMap[target.client] || 'Unknown client'}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      — {matterMap[target.matter] || 'Unknown matter'}
                    </span>
                  </div>
                  <Badge variant="outline">{formatCurrency(target.total)}</Badge>
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowDown className="h-5 w-5 text-muted-foreground" />
            </div>

            {/* Source invoices to be merged in */}
            <div>
              <h4 className="text-sm font-semibold mb-2">
                Invoices to merge in ({sources.length})
              </h4>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Matter</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sources.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                        <TableCell>{matterMap[inv.matter] || 'Unknown'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Summary */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="text-sm font-medium">Merged total</span>
              <span className="font-semibold">{formatCurrency(mergedTotal)}</span>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                • Invoices {sources.map((s) => s.invoice_number).join(', ')} will be removed.
              </p>
              <p>
                • All timesheets and costs will be moved to invoice {target.invoice_number}.
              </p>
              <p>
                • Invoice numbers after the removed ones will shift down by one each.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isMerging}>
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!validation.valid || isMerging}
          >
            {isMerging ? 'Merging...' : `Merge ${selectedList.length} Invoices`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
